import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useEffect, useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { livyJson } from 'src/lib/livyApi';
import { useLivyLive } from './LivyLive';
/** Strips handoff and rep-step (<propose .../>) tags from what is shown, including one still streaming in. */
export const withoutHandoff = content => content.replace(/\s*<handoff>[\s\S]*?<\/handoff>/g, '').replace(/\s*<handoff>[\s\S]*$/, '').replace(/\s*<propose\b[^>]*\/>/g, '').replace(/\s*<propose\b[^>]*$/, '');
/**
 * Livy's handoff to Livable support. Nothing reaches the team until the person confirms here; the proxy
 * checks that the request is theirs (/support-requests is keyed by the sub on their token).
 */

export function LivySupportCard(_ref) {
  let offer = _ref.offer;
  const _useState = useState('idle'),
    _useState2 = _slicedToArray(_useState, 2),
    state = _useState2[0],
    setState = _useState2[1];
  // "Talk to a person now" shows only when the proxy says so: someone on the team has the Live tab open, and for a
  // customer, live handoff is switched on.
  const _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    liveAvailable = _useState4[0],
    setLiveAvailable = _useState4[1];
  const _useLivyLive = useLivyLive(),
    onLive = _useLivyLive.onLive;
  useEffect(() => {
    // Only a chat that can go live asks (the drawer and the Livy page); anywhere else the card is email only.
    if (!onLive) return undefined;
    let gone = false;
    livyJson("/support-requests/".concat(encodeURIComponent(offer.requestId))).then(res => {
      var _res$request;
      if (gone) return;
      setLiveAvailable(Boolean(res === null || res === void 0 ? void 0 : res.liveAvailable));
      if ((res === null || res === void 0 || (_res$request = res.request) === null || _res$request === void 0 ? void 0 : _res$request.status) === 'live') {
        setState('live');
        // A resumed chat whose handoff is still on: start the banner and the poll again.
        if (res.live && (res.live.state === 'waiting' || res.live.state === 'joined')) onLive(res.live);
      }
    }).catch(() => undefined);
    return () => {
      gone = true;
    };
  }, [offer.requestId, onLive]);
  const createDecideHandler = decision => async () => {
    setState('saving');
    try {
      var _res$request2;
      const res = await livyJson('/support-requests', {
        method: 'POST',
        body: {
          requestId: offer.requestId,
          decision
        }
      });
      const status = res === null || res === void 0 || (_res$request2 = res.request) === null || _res$request2 === void 0 ? void 0 : _res$request2.status;
      if (status === 'live' && res.live) {
        onLive === null || onLive === void 0 || onLive(res.live);
        setState('live');
        return;
      }
      // A request the filer has picked up (filing, filed) was confirmed; anything unknown is not "sent".
      setState(status === 'dismissed' ? 'dismissed' : ['confirmed', 'filing', 'filed'].includes(status !== null && status !== void 0 ? status : '') ? 'confirmed' : 'error');
    } catch (_unused) {
      setState('error');
    }
  };
  return /*#__PURE__*/React.createElement(Box, {
    role: "group",
    "aria-label": "Send to Livable support",
    sx: {
      mt: '12px',
      p: '12px 14px',
      borderRadius: '10px',
      border: 1,
      borderColor: 'divider',
      bgcolor: 'action.hover'
    }
  }, state === 'live' ? /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 13.5
    }
  }, "Sent to Livable support, and someone has been asked to join this chat.") : state === 'confirmed' ? /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 13.5
    }
  }, "Sent to Livable support. They'll reply by email.") : state === 'dismissed' ? /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 13.5
    }
  }, "Not sent. You can ask again any time.") : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 13.5,
      fontWeight: 600
    }
  }, "Send this to Livable support?"), /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 13,
      color: 'text.secondary',
      mt: '4px',
      whiteSpace: 'pre-wrap'
    }
  }, offer.summary), state === 'error' && /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 12.5,
      color: 'error.main',
      mt: '6px'
    }
  }, "That didn't go through. Try again."), /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    spacing: "8px",
    sx: {
      mt: '10px'
    }
  }, /*#__PURE__*/React.createElement(Button, {
    size: "small",
    variant: "contained",
    disabled: state === 'saving',
    onClick: createDecideHandler('confirmed'),
    sx: {
      textTransform: 'none'
    }
  }, "Send to support"), liveAvailable && /*#__PURE__*/React.createElement(Button, {
    size: "small",
    variant: "outlined",
    disabled: state === 'saving',
    onClick: createDecideHandler('live'),
    sx: {
      textTransform: 'none'
    }
  }, "Talk to a person now"), /*#__PURE__*/React.createElement(Button, {
    size: "small",
    disabled: state === 'saving',
    onClick: createDecideHandler('dismissed'),
    sx: {
      textTransform: 'none',
      color: 'text.secondary',
      bgcolor: 'background.paper'
    }
  }, "Not now"))));
}